import EmblaCarousel, { type EmblaCarouselType } from 'embla-carousel';

const MOBILE_BREAKPOINT = 1024;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';

function setNodeActive(nodes: NodeListOf<HTMLElement>, stepNumber: number): void {
  nodes.forEach(node => {
    const active = node.dataset.step === String(stepNumber);
    node.classList.toggle('is-active', active);
    node.setAttribute('aria-pressed', String(active));
  });
}

function getStepNode(nodes: NodeListOf<HTMLElement>, stepNumber: number): HTMLElement | null {
  return Array.from(nodes).find(node => node.dataset.step === String(stepNumber)) ?? null;
}

function setCardActive(
  cards: NodeListOf<HTMLElement>,
  stepNumber: number,
  hideInactive = false
): void {
  cards.forEach(card => {
    const active = card.dataset.step === String(stepNumber);
    card.classList.toggle('is-active', active);

    if (hideInactive) {
      card.toggleAttribute('hidden', !active);
    } else {
      card.removeAttribute('hidden');
    }
  });
}

function getStepCard(cards: NodeListOf<HTMLElement>, stepNumber: number): HTMLElement | null {
  return Array.from(cards).find(card => card.dataset.step === String(stepNumber)) ?? null;
}

function setAccordionPanelInteractivity(panel: HTMLElement, interactive: boolean): void {
  panel.setAttribute('aria-hidden', interactive ? 'false' : 'true');

  panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach(element => {
    if (interactive) {
      const previousTabindex = element.getAttribute('data-accordion-tabindex');

      if (previousTabindex === null) {
        element.removeAttribute('tabindex');
      } else {
        element.setAttribute('tabindex', previousTabindex);
        element.removeAttribute('data-accordion-tabindex');
      }

      return;
    }

    if (!element.hasAttribute('data-accordion-tabindex')) {
      const currentTabindex = element.getAttribute('tabindex');

      if (currentTabindex !== null) {
        element.setAttribute('data-accordion-tabindex', currentTabindex);
      }
    }

    element.setAttribute('tabindex', '-1');
  });
}

function forceCollapseAccordion(trigger: HTMLButtonElement, panel: HTMLElement): void {
  trigger.setAttribute('aria-expanded', 'false');
  setAccordionPanelInteractivity(panel, false);
  panel.hidden = false;
  panel.removeAttribute('data-accordion-collapsing');
  panel.style.removeProperty('--accordion-panel-height');

  const previousTransition = panel.style.transition;
  panel.style.transition = 'none';
  panel.style.height = '0px';
  window.requestAnimationFrame(() => {
    panel.style.transition = previousTransition;
  });
}

function enforceExclusiveAccordion(card: HTMLElement, openedTrigger: HTMLButtonElement): void {
  const allTriggers = card.querySelectorAll<HTMLButtonElement>('[data-accordion-trigger]');
  allTriggers.forEach(trigger => {
    if (trigger === openedTrigger) return;
    if (trigger.getAttribute('aria-expanded') !== 'true') return;

    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? card.querySelector<HTMLElement>(`#${CSS.escape(panelId)}`) : null;

    if (panel) {
      forceCollapseAccordion(trigger, panel);
    } else {
      trigger.setAttribute('aria-expanded', 'false');
    }
  });
}

function initMobileExclusiveAccordions(root: HTMLElement): () => void {
  const cards = root.querySelectorAll<HTMLElement>('[data-infographic-card]');

  const handlers: Array<{ el: HTMLButtonElement; fn: () => void }> = [];

  cards.forEach(card => {
    const triggers = card.querySelectorAll<HTMLButtonElement>('[data-accordion-trigger]');
    triggers.forEach(trigger => {
      const fn = () => {
        queueMicrotask(() => {
          if (trigger.getAttribute('aria-expanded') === 'true') {
            enforceExclusiveAccordion(card, trigger);
          }
        });
      };
      trigger.addEventListener('click', fn);
      handlers.push({ el: trigger, fn });
    });
  });

  return (): void => {
    handlers.forEach(({ el, fn }) => el.removeEventListener('click', fn));
  };
}

export default function infiniteLoopInfographic(root: HTMLElement): () => void {
  const stepNodes = root.querySelectorAll<HTMLElement>('[data-infographic-node]');
  const stepCards = root.querySelectorAll<HTMLElement>('[data-infographic-card]');
  const panelsEl = root.querySelector<HTMLElement>('[data-infographic-panels]');
  const sliderEl = root.querySelector<HTMLElement>('[data-infographic-slider]');
  const closeBtns = root.querySelectorAll<HTMLButtonElement>('[data-infographic-close]');

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!panelsEl || stepCards.length === 0 || stepNodes.length === 0) {
    return () => {};
  }

  const panels = panelsEl;

  let activeStep = 1;
  let embla: EmblaCarouselType | null = null;
  let cleanupExclusiveAccordions: (() => void) | null = null;
  let isMobile = false;

  const controller = new AbortController();
  const { signal } = controller;

  function activateStep(stepNumber: number, source: 'node' | 'carousel' = 'node'): void {
    activeStep = stepNumber;
    root.dataset.activeStep = String(stepNumber);
    setNodeActive(stepNodes, stepNumber);

    root.removeAttribute('data-all-visible');

    if (isMobile) {
      const slideIndex = stepNumber - 1;
      if (embla && embla.selectedScrollSnap() !== slideIndex && source === 'node') {
        embla.scrollTo(slideIndex, prefersReducedMotion);

        if (panelsEl) {
          const header = document.querySelector<HTMLElement>('[data-module="header"]');
          const headerHeight = header ? header.getBoundingClientRect().height : 0;
          const top = panelsEl.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
          window.scrollTo({ top, behavior: prefersReducedMotion ? 'instant' : 'smooth' });
        }
      }
    } else {
      setCardActive(stepCards, stepNumber, true);
    }
  }

  function focusStepCloseButton(stepNumber: number): void {
    if (isMobile) return;

    const card = getStepCard(stepCards, stepNumber);
    card?.querySelector<HTMLButtonElement>('[data-infographic-close]')?.focus();
  }

  function setAllVisible(): void {
    root.setAttribute('data-all-visible', 'true');
    delete root.dataset.activeStep;
    setNodeActive(stepNodes, -1);
    setCardActive(stepCards, -1);
  }

  // Step node buttons
  stepNodes.forEach(node => {
    node.addEventListener(
      'click',
      event => {
        const step = Number(node.dataset.step);
        if (!step) return;
        activateStep(step);

        if (event.detail === 0) {
          focusStepCloseButton(step);
        }
      },
      { signal }
    );
  });

  stepCards.forEach(card => {
    card.addEventListener(
      'click',
      event => {
        const target = event.target;

        if (!(target instanceof HTMLElement)) return;
        if (!root.hasAttribute('data-all-visible')) return;
        if (target.closest('button, a, [data-accordion-trigger]')) return;

        const step = Number(card.dataset.step);
        if (!step) return;

        activateStep(step);
      },
      { signal }
    );
  });

  // Close buttons (desktop)
  closeBtns.forEach(btn => {
    btn.addEventListener(
      'click',
      () => {
        const step = Number(btn.closest<HTMLElement>('[data-infographic-card]')?.dataset.step);

        setAllVisible();

        if (step) {
          getStepNode(stepNodes, step)?.focus();
        }
      },
      { signal }
    );
  });

  // Mobile carousel dot button labels
  function updateDotLabels(): void {
    if (!sliderEl) return;

    const dots = sliderEl.querySelectorAll<HTMLButtonElement>('[data-carousel-dot]');
    dots.forEach(dot => {
      const startIndex = Number(dot.dataset.carouselDot ?? 0);
      const step = Array.from(stepCards)[startIndex];
      const stepNum = step?.dataset.step ?? String(startIndex + 1);
      const node = root.querySelector<HTMLElement>(
        `[data-infographic-node][data-step="${stepNum}"]`
      );
      const label =
        node?.querySelector('.infinite-loop-infographic__node-text')?.textContent?.trim() ?? '';
      dot.setAttribute('aria-label', `Step ${stepNum}: ${label}`);
    });
  }

  function destroyMobile(): void {
    cleanupExclusiveAccordions?.();
    cleanupExclusiveAccordions = null;

    if (embla) {
      embla.destroy();
      embla = null;
    }

    if (sliderEl) {
      sliderEl.setAttribute('hidden', 'true');
    }
  }

  function initMobile(): void {
    isMobile = true;

    if (sliderEl) {
      sliderEl.removeAttribute('hidden');
    }

    // Show all cards (carousel mode)
    stepCards.forEach(card => card.removeAttribute('hidden'));

    embla = EmblaCarousel(panels, {
      loop: false,
      align: 'start',
      dragFree: false,
      containScroll: 'trimSnaps'
    });

    // Sync dot labels after render
    const prevBtns = sliderEl
      ? [...sliderEl.querySelectorAll<HTMLButtonElement>('[data-carousel-prev]')]
      : [];
    const nextBtns = sliderEl
      ? [...sliderEl.querySelectorAll<HTMLButtonElement>('[data-carousel-next]')]
      : [];
    const dotsRoots = sliderEl
      ? [...sliderEl.querySelectorAll<HTMLElement>('[data-carousel-dots]')]
      : [];
    const statusEl = sliderEl?.querySelector<HTMLElement>('[data-carousel-status]') ?? null;

    function setButtonState(btn: HTMLButtonElement, disabled: boolean): void {
      btn.disabled = disabled;
      btn.setAttribute('aria-disabled', String(disabled));
    }

    function renderDots(): void {
      const count = embla!.scrollSnapList().length;
      dotsRoots.forEach(dotsRoot => {
        dotsRoot.innerHTML = '';
        for (let i = 0; i < count; i++) {
          const dot = document.createElement('button');
          const dotCore = document.createElement('span');
          dot.type = 'button';
          dot.dataset.carouselDot = String(i);
          dot.className = 'carousel-controls__dot';
          dotCore.className = 'carousel-controls__dot-core';
          dot.setAttribute('aria-current', 'false');
          dot.appendChild(dotCore);
          dot.addEventListener('click', () => embla?.scrollTo(i));
          dotsRoot.appendChild(dot);
        }
      });
      updateDotLabels();
    }

    function syncUi(): void {
      if (!embla) return;
      const selected = embla.selectedScrollSnap();
      const count = embla.scrollSnapList().length;
      const card = Array.from(stepCards)[selected];
      const stepNum = Number(card?.dataset.step ?? selected + 1);

      activeStep = stepNum;
      root.dataset.activeStep = String(stepNum);
      setNodeActive(stepNodes, stepNum);

      prevBtns.forEach(btn => setButtonState(btn, selected === 0));
      nextBtns.forEach(btn => setButtonState(btn, selected === count - 1));

      if (statusEl) {
        statusEl.textContent = `Step ${stepNum} of ${count}`;
      }

      dotsRoots.forEach(dotsRoot => {
        dotsRoot.querySelectorAll<HTMLButtonElement>('[data-carousel-dot]').forEach((dot, i) => {
          const active = i === selected;
          dot.toggleAttribute('data-active', active);
          dot.setAttribute('aria-current', String(active));
        });
      });
    }

    prevBtns.forEach(btn =>
      btn.addEventListener('click', () => embla?.scrollTo((embla?.selectedScrollSnap() ?? 1) - 1))
    );
    nextBtns.forEach(btn =>
      btn.addEventListener('click', () => embla?.scrollTo((embla?.selectedScrollSnap() ?? 0) + 1))
    );

    function collapseCardAccordions(card: HTMLElement): void {
      card.querySelectorAll<HTMLButtonElement>('[data-accordion-trigger]').forEach(trigger => {
        if (trigger.getAttribute('aria-expanded') !== 'true') return;
        const panelId = trigger.getAttribute('aria-controls');
        const panel = panelId ? card.querySelector<HTMLElement>(`#${CSS.escape(panelId)}`) : null;
        if (panel) forceCollapseAccordion(trigger, panel);
      });
    }

    function collapseInactiveAccordions(): void {
      if (!embla) return;
      const selected = embla.selectedScrollSnap();
      Array.from(stepCards).forEach((card, i) => {
        if (i !== selected) collapseCardAccordions(card);
      });
    }

    embla.on('init', renderDots);
    embla.on('reInit', renderDots);
    embla.on('select', syncUi);
    embla.on('select', collapseInactiveAccordions);

    renderDots();
    syncUi();

    cleanupExclusiveAccordions = initMobileExclusiveAccordions(root);
  }

  function initDesktop(): void {
    isMobile = false;

    if (sliderEl) {
      sliderEl.setAttribute('hidden', 'true');
    }

    activateStep(activeStep);
  }

  function onResize(): void {
    const nowMobile = window.innerWidth < MOBILE_BREAKPOINT;

    if (nowMobile && !isMobile) {
      destroyMobile();
      initMobile();
    } else if (!nowMobile && isMobile) {
      destroyMobile();
      initDesktop();
    }
  }

  // Initial setup
  if (window.innerWidth < MOBILE_BREAKPOINT) {
    initMobile();
  } else {
    initDesktop();
  }

  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(document.body);

  // Keyboard: left/right arrow on node buttons
  root.addEventListener(
    'keydown',
    event => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (!target.closest('[data-infographic-node]')) return;

      const nodes = [...stepNodes];
      const currentIndex = nodes.findIndex(n => n.dataset.step === String(activeStep));

      if (event.key === 'ArrowLeft' && currentIndex > 0) {
        event.preventDefault();
        const prev = Number(nodes[currentIndex - 1]?.dataset.step);
        activateStep(prev);
        (nodes[currentIndex - 1] as HTMLElement)?.focus();
      } else if (event.key === 'ArrowRight' && currentIndex < nodes.length - 1) {
        event.preventDefault();
        const next = Number(nodes[currentIndex + 1]?.dataset.step);
        activateStep(next);
        (nodes[currentIndex + 1] as HTMLElement)?.focus();
      }
    },
    { signal }
  );

  return (): void => {
    controller.abort();
    resizeObserver.disconnect();
    destroyMobile();
  };
}
